/**
 * base_carousel.js — initialises all [data-bc-carousel] elements.
 * Loads Swiper from CDN once per page, then reads data-bc-options JSON
 * from each carousel element to build the Swiper config.
 * Bundled via Vite's import.meta.glob in templates/main.js.
 */

const SWIPER_CSS = 'https://media.seekunique.co/websites-cdn-assets/swiper%4011-swiper-bundle.min.css';
const SWIPER_JS  = 'https://media.seekunique.co/websites-cdn-assets/swiper%4011-swiper-bundle.min.js';

function loadSwiper() {
    return new Promise(resolve => {
        if (window.Swiper) return resolve();

        if (!document.querySelector(`link[href="${SWIPER_CSS}"]`)) {
            const link = Object.assign(document.createElement('link'), {
                rel: 'stylesheet', href: SWIPER_CSS, media: 'print',
            });
            link.addEventListener('load', () => { link.media = 'all'; });
            document.head.appendChild(link);
        }

        if (document.querySelector(`script[src="${SWIPER_JS}"]`)) return resolve();
        const script = Object.assign(document.createElement('script'), { src: SWIPER_JS });
        script.addEventListener('load', resolve);
        document.head.appendChild(script);
    });
}

function intBool(v, def = false) {
    if (v === undefined || v === null) return def;
    return v === 1 || v === true;
}

function buildSwiperConfig(el, opts, prefersReducedMotion) {
    const autoplay   = opts.autoplay        || {};
    const effect     = opts.effect          || {};
    const effectName = effect.name          || 'slide';
    const baseDelay  = autoplay.delay       || 5000;

    const cfg = {
        direction: opts.direction || 'horizontal',
        loop:      intBool(opts.loop, true),
        speed:     prefersReducedMotion ? 0 : (opts.speed || 500),
        effect:    prefersReducedMotion ? 'slide' : effectName,
        a11y: {
            enabled:            true,
            prevSlideMessage:   'Previous slide',
            nextSlideMessage:   'Next slide',
            firstSlideMessage:  'This is the first slide',
            lastSlideMessage:   'This is the last slide',
        },
        on: {
            init() {
                if (prefersReducedMotion || !intBool(autoplay.enabled)) return;
                this.params.autoplay.delay = this.realIndex === 0 ? baseDelay * 3 : baseDelay;
                this.autoplay.start();
            },
            slideChange() {
                if (prefersReducedMotion || !intBool(autoplay.enabled)) return;
                this.params.autoplay.delay = this.realIndex === 0 ? baseDelay * 3 : baseDelay;
            },
        },
    };

    const pagination = opts.pagination || {};
    if (intBool(pagination.enabled)) {
        cfg.pagination = {
            el:        el.querySelector('.swiper-pagination'),
            type:      pagination.type || 'bullets',
            clickable: intBool(pagination.clickable, true),
        };
    }

    const navigation = opts.navigation || {};
    if (intBool(navigation.enabled)) {
        cfg.navigation = {
            nextEl:      el.querySelector('.swiper-button-next'),
            prevEl:      el.querySelector('.swiper-button-prev'),
            hideOnClick: intBool(navigation.hide_on_click, true),
        };
    }

    const scrollbar = opts.scrollbar || {};
    if (intBool(scrollbar.enabled)) {
        cfg.scrollbar = {
            el:            el.querySelector('.swiper-scrollbar'),
            draggable:     intBool(scrollbar.draggable, true),
            snapOnRelease: intBool(scrollbar.snap_on_release, true),
        };
    }

    if (intBool(autoplay.enabled) && !prefersReducedMotion) {
        cfg.autoplay = {
            delay:                baseDelay,
            disableOnInteraction: intBool(autoplay.disable_on_interaction),
            pauseOnMouseEnter:    intBool(autoplay.pause_on_mouse_enter),
            stopOnLastSlide:      intBool(autoplay.stop_on_last_slide),
            reverseDirection:     intBool(autoplay.reverse_direction),
        };
    }

    const grid = opts.grid || {};
    if (intBool(grid.enabled)) {
        cfg.grid = { rows: grid.rows || 1, fill: grid.fill || 'row' };
    }

    const freemode = opts.freemode || {};
    if (intBool(freemode.enabled)) {
        cfg.freeMode = {
            sticky:                intBool(freemode.sticky),
            momentumRatio:         freemode.momentum_ratio         || 1,
            momentumVelocityRatio: freemode.momentum_velocity_ratio || 1,
        };
    }

    if (effectName === 'fade') {
        cfg.fadeEffect = { crossFade: intBool(effect.fade_effect?.cross_fade, true) };
    } else if (effectName === 'cube') {
        const ce = effect.cube_effect || {};
        cfg.cubeEffect = {
            slideShadows:  intBool(ce.slide_shadows, true),
            shadow:        intBool(ce.shadow, true),
            shadowOffset:  ce.shadow_offset || 20,
            shadowScale:   ce.shadow_scale  || 0.94,
        };
    } else if (effectName === 'coverflow') {
        const ce = effect.coverflow_effect || {};
        cfg.coverflowEffect = {
            rotate:       ce.rotate    || 50,
            stretch:      ce.stretch   || 0,
            depth:        ce.depth     || 100,
            modifier:     ce.modifier  || 1,
            slideShadows: intBool(ce.slide_shadows, true),
        };
    } else if (effectName === 'flip') {
        const fe = effect.flip_effect || {};
        cfg.flipEffect = {
            slideShadows:    intBool(fe.slide_shadows, true),
            limitRotation:   intBool(fe.limit_rotation),
        };
    } else if (effectName === 'cards') {
        const ce = effect.cards_effect || {};
        cfg.cardsEffect = {
            perSlideOffset: ce.per_slide_offset || 15,
            perSlideRotate: ce.per_slide_rotate || 5,
            slideShadows:   intBool(ce.slide_shadows, true),
        };
    } else if (effectName === 'creative') {
        const ce = effect.creative_effect || {};
        cfg.creativeEffect = {
            prev: {
                translate: ce.prev?.translate || [0, 0, -400],
                rotate:    ce.prev?.rotate    || [0, 0,  180],
                opacity:   ce.prev?.opacity   ?? 0,
            },
            next: {
                translate: ce.next?.translate || [0, 0,  400],
                rotate:    ce.next?.rotate    || [0, 0, -180],
                opacity:   ce.next?.opacity   ?? 0,
            },
        };
    }

    const hashNav = opts.hash_navigation || {};
    if (intBool(hashNav.enabled)) {
        cfg.hashNavigation = {
            watchState:   intBool(hashNav.watch_state),
            replaceState: intBool(hashNav.replace_state),
        };
    }

    const zoom = opts.zoom || {};
    if (intBool(zoom.enabled)) {
        cfg.zoom = {
            maxRatio: zoom.max_ratio || 3,
            minRatio: zoom.min_ratio || 1,
            toggle:   intBool(zoom.toggle, true),
        };
    }

    const keyboard = opts.keyboard || {};
    if (intBool(keyboard.enabled)) {
        cfg.keyboard = {
            enabled:        true,
            onlyInViewport: intBool(keyboard.only_in_viewport, true),
        };
    }

    return cfg;
}

function initCarousels() {
    const carousels = document.querySelectorAll('[data-bc-carousel]');
    if (!carousels.length) return;

    loadSwiper().then(() => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        carousels.forEach(el => {
            if (el._bcInit) return;
            el._bcInit = true;
            const scriptTag = el.querySelector('script[data-bc-options]');
            const opts = JSON.parse(scriptTag ? scriptTag.textContent : '{}');
            new Swiper(el, buildSwiperConfig(el, opts, prefersReducedMotion));
        });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
} else {
    initCarousels();
}
